// src/features/shared/components/ErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { buildErrorReport, logErrorReport, type ClientErrorReport } from '@/lib/clientErrorReport';
import { ErrorReportDetails } from '../ErrorReportDetails';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  /** What the screen shows and logs so the crash can be diagnosed (DEF-0mvnmww1e003). */
  report: ClientErrorReport | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      report: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
      report: buildErrorReport(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Same reference as the screen shows, now with the component stack.
    const fresh = buildErrorReport(error, { componentStack: errorInfo?.componentStack });
    const prior = this.state.report;
    const report = prior ? { ...fresh, ref: prior.ref, at: prior.at, lastRequest: prior.lastRequest } : fresh;
    logErrorReport(report);
    this.setState({ error, errorInfo, report });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      report: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <div className="min-h-screen bg-muted flex items-center justify-center px-4">
          <div className="max-w-md w-full">
            <div className="bg-card rounded-lg shadow-lg p-6">
              {/* Error Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-danger-soft rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-danger" />
                </div>
              </div>

              {/* Error Message */}
              <h1 className="text-h2 text-charcoal text-center mb-2">
                Oops! Something went wrong
              </h1>
              <p className="text-muted-foreground text-center mb-6">
                We're sorry for the inconvenience. An unexpected error has occurred.
              </p>

              {/* Always shown: a reference to quote and what broke, never a body or token. */}
              {this.state.report && (
                <div className="mb-6 flex justify-center">
                  <ErrorReportDetails report={this.state.report} />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={this.handleReset}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="flex-1 flex items-center justify-center px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </button>
              </div>
            </div>

            {/* Support Link */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              If this problem persists, please{' '}
              <a href="/support" className="text-blue-600 hover:text-blue-700 font-medium">
                contact support
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for functional components
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
}

export default ErrorBoundary;